/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { type tokens } from '@neo4j-ndl/base';
import classNames from 'classnames';

import { type PolymorphicCommonProps } from '../_common/types';

export type TypographyVariants = keyof typeof tokens.typography;

type TypographyProps = {
  /** The typography variant to use for the text. */
  variant: TypographyVariants;
  /** Content displayed inside the component */
  children?: React.ReactNode;
};

export const Typography = <T extends React.ElementType = 'span'>(
  props: PolymorphicCommonProps<T, TypographyProps>,
): React.ReactElement | null => {
  const {
    as,
    className = '',
    children,
    variant,
    htmlAttributes,
    ref,
    ...restProps
  } = props;

  const classes = classNames(`n-${variant}`, className);
  const Component = as ?? 'span';

  return (
    <Component className={classes} ref={ref} {...restProps} {...htmlAttributes}>
      {children}
    </Component>
  );
};
